import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Badge, Button, IconArrowLeft, Notice, Plate, SelectField, TextField, type BadgeProps } from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { useCan } from "../auth.ts";
import { ConfirmDialog, type Confirm } from "../ConfirmDialog.tsx";
import { formatDate, formatMoney, formatNumber, t, type MessageKey } from "../i18n.ts";
import { ProductSearch } from "../stock/RequestDraftPage.tsx";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import type { ProductRef } from "../stock/types.ts";
import { serviceApi, type PartAction, type Repair, type RepairState } from "./api.ts";

const route = getRouteApi("/app/service/repairs/$id");

const STATE: Record<RepairState, { key: MessageKey; tone: BadgeProps["tone"] }> = {
  CONFIRMED: { key: "repair.state.CONFIRMED", tone: "info" },
  UNDER_REPAIR: { key: "repair.state.UNDER_REPAIR", tone: "strong" },
  DONE: { key: "repair.state.DONE", tone: "success" },
  CANCELLED: { key: "repair.state.CANCELLED", tone: "neutral" },
};

const ACTIONS: PartAction[] = ["ADD", "REMOVE", "SCRAP"];
const ACTION: Record<PartAction, MessageKey> = { ADD: "repair.part.ADD", REMOVE: "repair.part.REMOVE", SCRAP: "repair.part.SCRAP" };

const repairQuery = (id: string) => ({ queryKey: ["service", "repair", id], queryFn: () => serviceApi.repair(id), retry: false });

/**
 * Документ ремонта (ПС-10...ПС-14): детали «Додати / Зняти / Утилізувати»
 * с «Робочої зони» «Ремонту», работы из шагов карты сборки без порядка и
 * ОТК после всех работ, завершение с несделанными работами проходит с
 * отметкой, отмена гасит работы. Стоимость (детали и доля ФОТ SRV из
 * закрытого месяца) только с правом себестоимости.
 */
export function RepairPage() {
  const { id } = route.useParams();
  const queryClient = useQueryClient();
  const can = useCan();
  const repair = useQuery(repairQuery(id));
  const cost = useQuery({ queryKey: ["service", "cost", id], queryFn: () => serviceApi.cost(id), enabled: can("cost.read") });
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const run = useMutation({ mutationFn: (fn: () => Promise<Repair>) => fn() });
  const apply = (r: Repair, n?: NoticeState) => {
    queryClient.setQueryData(repairQuery(id).queryKey, r);
    void queryClient.invalidateQueries({ queryKey: ["service"] });
    setNotice(n ?? null);
  };
  const act = (fn: () => Promise<Repair>, ok?: (r: Repair) => NoticeState) => run.mutate(fn, { onSuccess: (r) => apply(r, ok?.(r)), onError: (e) => setNotice(errorNotice(e)) });

  const r = repair.data;
  if (repair.error) return <Notice tone="danger" title={errorNotice(repair.error).title} className="page" />;
  if (!r) return null;
  const open = r.state === "CONFIRMED" || r.state === "UNDER_REPAIR";
  const edit = open && can("service.repair");
  const unfinished = r.works.filter((w) => w.state !== "DONE" && w.state !== "CANCELLED" && w.state !== "SKIPPED");

  return (
    <div className="page order">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/production/unit" search={{ serial: r.serial }} aria-label={t("repair.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title w-num">{r.number}</h1>
        <Badge tone={STATE[r.state].tone}>{t(STATE[r.state].key)}</Badge>
      </div>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {r.openWorksAtClose.length > 0 ? <Notice tone="warning" title={t("unit.unfinished", { works: r.openWorksAtClose.join(", ") })} className="stock-notice" /> : null}

      <Plate cut="lg" className="order-head">
        <dl className="order-meta">
          <div className="order-meta-item">
            <dt>{t("repair.serial")}</dt>
            <dd>
              <Link to="/production/unit" search={{ serial: r.serial }} className="notice-link w-num">
                {r.serial}
              </Link>
            </dd>
          </div>
          <div className="order-meta-item">
            <dt>{t("repair.product")}</dt>
            <dd>{r.productName}</dd>
          </div>
          <div className="order-meta-item">
            <dt>{t("repair.created")}</dt>
            <dd>{formatDate(r.createdOn)}</dd>
          </div>
          {r.closedOn ? (
            <div className="order-meta-item">
              <dt>{t("repair.closed")}</dt>
              <dd>{formatDate(r.closedOn)}</dd>
            </div>
          ) : null}
          {r.reason ? (
            <div className="order-meta-item">
              <dt>{t("repair.reason")}</dt>
              <dd>{r.reason}</dd>
            </div>
          ) : null}
        </dl>
      </Plate>

      {cost.data ? (
        <section className="stock-section" aria-labelledby="repair-cost">
          <h2 id="repair-cost" className="desk-group-title">
            {t("repair.cost")}
          </h2>
          <dl className="cost-sum">
            <div>
              <dt>{t("repair.cost.parts")}</dt>
              <dd className="w-num">{formatMoney(cost.data.parts, "UAH")}</dd>
            </div>
            <div>
              <dt>{t("repair.cost.labor")}</dt>
              <dd className="w-num">{cost.data.labor == null ? t("repair.cost.laborLater") : formatMoney(cost.data.labor, "UAH")}</dd>
            </div>
            <div data-total="">
              <dt>{t("repair.cost.total")}</dt>
              <dd className="w-num">{formatMoney(cost.data.total, "UAH")}</dd>
            </div>
          </dl>
        </section>
      ) : null}

      <section className="stock-section" aria-labelledby="repair-parts">
        <h2 id="repair-parts" className="desk-group-title">
          {t("repair.parts")}
        </h2>
        {r.parts.length === 0 ? <p className="w-muted">{t("repair.noParts")}</p> : null}
        <ul className="payments">
          {r.parts.map((p) => (
            <li key={p.id} className="payment">
              <span>
                <Badge tone={p.action === "ADD" ? "strong" : p.action === "SCRAP" ? "danger" : "neutral"}>{t(ACTION[p.action])}</Badge>
              </span>
              <span className="payment-ref">
                {p.name}
                {p.partSerial ? <span className="w-num w-muted">{` ${p.partSerial}`}</span> : null}
                <span className="w-muted">{` · ${p.employee} · ${formatDate(p.at.slice(0, 10))}`}</span>
                {p.note ? <span className="w-muted">{` · ${p.note}`}</span> : null}
              </span>
              <span className="w-num payment-sum">{`× ${formatNumber(p.qty)}`}</span>
            </li>
          ))}
        </ul>
        {edit ? <AddPart onAdd={(body) => act(() => serviceApi.part(r.id, body))} pending={run.isPending} /> : null}
      </section>

      <section className="stock-section" aria-labelledby="repair-works">
        <h2 id="repair-works" className="desk-group-title">
          {t("repair.works")}
        </h2>
        {r.works.length === 0 ? <p className="w-muted">{t("repair.noWorks")}</p> : null}
        <ol className="steps">
          {r.works.map((w) => (
            <li key={w.opId} className="step" data-closed={w.state === "DONE" ? "" : undefined}>
              <span className="step-name">
                {w.name}
                {w.attempt > 1 ? ` #${String(w.attempt)}` : ""}
                {w.note ? <span className="w-muted step-skill">{w.note}</span> : null}
              </span>
              <span className="step-state" data-state={w.state}>
                {w.state}
              </span>
              <span className="w-muted step-who">{w.employee ?? ""}</span>
              <span className="w-num step-time">{w.minutes > 0 ? t("unit.minutes", { minutes: formatNumber(w.minutes) }) : ""}</span>
            </li>
          ))}
        </ol>
        {edit ? <AddWorks repairId={r.id} onAdd={(body) => act(() => serviceApi.works(r.id, body), () => ({ tone: "success", title: t("repair.worksAdded") }))} pending={run.isPending} /> : null}
      </section>

      {edit ? (
        <div className="order-actions">
          <Button
            variant="primary"
            disabled={run.isPending}
            onClick={() =>
              unfinished.length > 0
                ? setConfirm({
                    title: t("repair.close.title"),
                    body: t("repair.close.unfinished", { works: unfinished.map((w) => w.name).join(", ") }),
                    action: t("repair.close"),
                    run: () => act(() => serviceApi.close(r.id)),
                  })
                : act(() => serviceApi.close(r.id), () => ({ tone: "success", title: t("repair.closed.ok") }))
            }
          >
            {t("repair.close")}
          </Button>
          <Button
            variant="ghost"
            disabled={run.isPending}
            onClick={() =>
              setConfirm({ title: t("repair.cancel.title", { repair: r.number }), body: t("repair.cancel.body"), action: t("repair.cancel"), run: () => act(() => serviceApi.cancel(r.id)) })
            }
          >
            {t("repair.cancel")}
          </Button>
        </div>
      ) : null}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

type PartBody = Parameters<typeof serviceApi.part>[1];

/** Строка детали: «Додати» с «Робочої зони», «Зняти» на «Карантин», «Утилізувати» списанием (ПС-11). */
function AddPart({ onAdd, pending }: { onAdd: (body: PartBody) => void; pending: boolean }) {
  const [action, setAction] = useState<PartAction>("ADD");
  const [product, setProduct] = useState<ProductRef | null>(null);
  const [qty, setQty] = useState("1");
  const [partSerial, setPartSerial] = useState("");
  const [note, setNote] = useState("");
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    if (!product) return;
    onAdd({ action, productId: product.id, qty: parseQty(qty) ?? 0, partSerial: partSerial.trim() || null, note: note.trim() || null });
    setProduct(null);
    setQty("1");
    setPartSerial("");
    setNote("");
  };
  if (!product) return <ProductSearch onPick={setProduct} />;
  return (
    <form className="stock-form repair-part" onSubmit={submit}>
      <p className="sales-picked">
        <span className="w-num">{product.sku}</span> {product.name}{" "}
        <Button variant="ghost" type="button" onClick={() => setProduct(null)}>
          {t("sales.change")}
        </Button>
      </p>
      <SelectField label={t("repair.part.action")} value={action} onChange={(e) => setAction(e.target.value as PartAction)} options={ACTIONS.map((a) => ({ value: a, label: t(ACTION[a]) }))} />
      <TextField label={t("sales.qty")} inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} required />
      <TextField label={t("repair.part.serial")} value={partSerial} onChange={(e) => setPartSerial(e.target.value)} autoComplete="off" />
      <TextField label={t("repair.part.note")} value={note} onChange={(e) => setNote(e.target.value)} autoComplete="off" />
      <Button type="submit" variant="primary" disabled={pending}>
        {t("repair.part.add")}
      </Button>
    </form>
  );
}

type WorksBody = Parameters<typeof serviceApi.works>[1];

/**
 * «Додати роботи» из шагов карты сборки изделия (SRV-02). Конструктор
 * отдаёт только названия шагов: шаг прошивки узнаётся по названию, ему
 * нужна версия прошивки при отметке (ПС-12).
 */
function AddWorks({ repairId, onAdd, pending }: { repairId: string; onAdd: (body: WorksBody) => void; pending: boolean }) {
  const [open, setOpen] = useState(false);
  const steps = useQuery({ queryKey: ["service", "constructor", repairId], queryFn: () => serviceApi.constructor(repairId), enabled: open });
  const [picked, setPicked] = useState<string[]>([]);
  const [qc, setQc] = useState(true);
  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {t("repair.addWorks")}
      </Button>
    );
  }
  return (
    <form
      className="stock-form"
      onSubmit={(e) => {
        e.preventDefault();
        onAdd({ qualityCheck: qc, works: picked.map((name) => ({ name, firmware: /прошив/i.test(name) })) });
        setPicked([]);
        setOpen(false);
      }}
    >
      {steps.error ? <Notice tone="danger" title={errorNotice(steps.error).title} /> : null}
      {steps.data?.map((name) => (
        <label key={name} className="check">
          <input type="checkbox" checked={picked.includes(name)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, name] : p.filter((x) => x !== name)))} />
          {name}
        </label>
      ))}
      <label className="check">
        <input type="checkbox" checked={qc} onChange={(e) => setQc(e.target.checked)} />
        {t("repair.qualityCheck")}
      </label>
      <Button type="submit" variant="primary" disabled={pending || picked.length === 0}>
        {t("repair.addWorks")}
      </Button>
    </form>
  );
}
